import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Link2, Plus, Copy, QrCode, BarChart3, Pencil, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PageHeader, EmptyState, Spinner, EthicsNote } from "../components/common";
import { FormDialog } from "../components/FormDialog";
import { useResource } from "../hooks/useResource";
import { api, shortUrl, copy, fmtDate } from "../lib/api";

const QrDialog = ({ link, onClose }) => {
  const ref = useRef(null);
  const download = () => {
    const a = document.createElement("a");
    a.href = ref.current.toDataURL("image/png");
    a.download = `qr-${link.code}.png`;
    a.click();
  };
  return (
    <Dialog open={!!link} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-white sm:max-w-sm" data-testid="qr-dialog">
        <DialogHeader><DialogTitle className="font-display">QR Code • {link?.title}</DialogTitle></DialogHeader>
        {link && (
          <div className="flex flex-col items-center gap-4">
            <div className="p-4 rounded-2xl bg-white border-4 border-[#D4AF37]"><QRCodeCanvas ref={ref} value={shortUrl(link.code)} size={220} level="H" fgColor="#09090B" marginSize={1} /></div>
            <div className="text-xs font-mono text-zinc-400 break-all text-center">{shortUrl(link.code)}</div>
            <Button className="btn-gold rounded-full w-full" onClick={download} data-testid="qr-download-button"><Download className="h-4 w-4 mr-1" />Baixar PNG</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const StatsDialog = ({ link, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => { if (link) api.get(`/links/${link.id}/stats`).then((r) => setData(r.data)); else setData(null); }, [link]);
  return (
    <Dialog open={!!link} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-white sm:max-w-lg" data-testid="stats-dialog">
        <DialogHeader><DialogTitle className="font-display">Cliques • {link?.title}</DialogTitle></DialogHeader>
        {data && (
          <>
            <div className="text-sm text-zinc-400">Total: <span className="text-amber-300 font-bold">{data.link.clicks}</span> • Último clique: {data.link.last_click_at ? new Date(data.link.last_click_at).toLocaleString("pt-BR") : "-"}</div>
            <div className="h-48"><ResponsiveContainer><BarChart data={data.series.map((s) => ({ ...s, d: s.date.slice(5) }))}><XAxis dataKey="d" stroke="#71717A" fontSize={10} /><Tooltip contentStyle={{ background: "#121215", border: "1px solid #3f3f46" }} /><Bar dataKey="value" name="cliques" fill="#D4AF37" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer></div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Links() {
  const r = useResource("links");
  const [campaigns, setCampaigns] = useState([]);
  const [params, setParams] = useSearchParams();
  const [dlg, setDlg] = useState({ open: false, item: null });
  const [qr, setQr] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/campaigns").then((x) => setCampaigns(x.data));
    if (params.get("url")) { setDlg({ open: true, item: { target_url: params.get("url"), title: params.get("title") || "" }, isNew: true }); setParams({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fields = [
    { name: "title", label: "Nome do link", required: true },
    { name: "target_url", label: "URL de destino (seu link de afiliado)", type: "url", required: true },
    { name: "campaign_id", label: "Campanha", type: "select", placeholder: "Nenhuma", options: campaigns.map((c) => ({ value: c.id, label: c.name })) },
    { name: "utm_source", label: "Origem (utm_source)", placeholder: "instagram", half: true },
    { name: "utm_medium", label: "Mídia (utm_medium)", placeholder: "bio", half: true },
    { name: "utm_campaign", label: "Campanha UTM (utm_campaign)", placeholder: "promo_junho" },
  ];
  const editing = dlg.item?.id;

  return (
    <div>
      <PageHeader eyebrow="Rastreamento" title="Links & QR Code" desc="Links curtos com UTM, QR Code para baixar e contagem de cliques reais."
        action={<Button className="btn-gold rounded-full h-11 px-5" onClick={() => setDlg({ open: true, item: null })} data-testid="link-add-button"><Plus className="h-4 w-4 mr-1" />Novo link</Button>} />
      {r.loading ? <Spinner /> : r.items.length === 0 ? (
        <EmptyState icon={Link2} title="Nenhum link criado" desc="Crie links rastreáveis para seus produtos e compartilhe em cada rede." />
      ) : (
        <div className="grid gap-3">
          {r.items.map((l) => (
            <div key={l.id} className="card-lux p-4 flex flex-col sm:flex-row sm:items-center gap-3 rise" data-testid={`link-row-${l.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{l.title}</div>
                <div className="text-xs font-mono text-amber-300/90 truncate" data-testid={`link-short-${l.id}`}>{shortUrl(l.code)}</div>
                <div className="text-[11px] text-zinc-500 truncate">→ {l.final_url}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-center px-3"><div className="font-display text-2xl font-extrabold gold-text" data-testid={`link-clicks-${l.id}`}>{l.clicks}</div><div className="text-[10px] text-zinc-500">cliques</div></div>
                <div className="text-[10px] text-zinc-500 hidden lg:block w-24">Criado {fmtDate(l.created_at)}</div>
                <div className="flex">
                  <button className="p-2 text-zinc-400 hover:text-white" onClick={() => copy(shortUrl(l.code)).then(() => toast.success("Link copiado!"))} data-testid={`link-copy-${l.id}`}><Copy className="h-4 w-4" /></button>
                  <button className="p-2 text-zinc-400 hover:text-amber-400" onClick={() => setQr(l)} data-testid={`link-qr-${l.id}`}><QrCode className="h-4 w-4" /></button>
                  <button className="p-2 text-zinc-400 hover:text-amber-400" onClick={() => setStats(l)} data-testid={`link-stats-${l.id}`}><BarChart3 className="h-4 w-4" /></button>
                  <button className="p-2 text-zinc-400 hover:text-amber-400" onClick={() => setDlg({ open: true, item: l })} data-testid={`link-edit-${l.id}`}><Pencil className="h-4 w-4" /></button>
                  <button className="p-2 text-zinc-400 hover:text-rose-400" onClick={() => r.remove(l.id)} data-testid={`link-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-6"><EthicsNote>Cliques de robôs e pré-visualizações (WhatsApp, Facebook) são ignorados. Não há opção de adicionar cliques manualmente — as métricas são sempre reais.</EthicsNote></div>
      <FormDialog open={dlg.open} onOpenChange={(o) => setDlg((s) => ({ ...s, open: o }))} title={editing ? "Editar link" : "Novo link rastreável"} fields={fields} initial={dlg.item} testid="link-form"
        onSubmit={(b) => r.save(b, editing)} />
      <QrDialog link={qr} onClose={() => setQr(null)} />
      <StatsDialog link={stats} onClose={() => setStats(null)} />
    </div>
  );
}
